import { Fragment } from "react";
import { toDateInputValue } from "../../utils/enrollmentDate";

const sections = {
  solicitud: [
    ["Datos del alumno/a", /^(student_|birth_|nationality|blood_group)/],
    ["Domicilio y contacto", /^(address|locality|postal_code|province|home_phone|alternate_phone)/],
    ["Nivel inicial", /^initial_/],
    ["Nivel primario", /^primary_/],
    ["Nivel secundario", /^secondary_/],
    ["Bautismo", /^baptism_/],
    ["Primera comunión", /^communion_/],
    ["Confirmación", /^confirmation_/],
    ["Responsable 1", /^guardian_1_/],
    ["Responsable 2", /^guardian_2_/],
    ["CONTRATO DE ADHESIÓN (Responsable económico)", /^contract_/],
  ],
  anexo: [
    ["Datos del alumno/a", /^student_/],
    ["Persona autorizada 1", /^authorized_1_/],
    ["Persona autorizada 2", /^authorized_2_/],
    ["Transporte escolar", /^transport_/],
    ["Responsable", /^guardian_/],
  ],
};

const sacramentSectionsByEntryLevel = {
  initial: new Set(["Bautismo"]),
  primary: new Set(["Bautismo", "Primera comunión"]),
  secondary: new Set(["Bautismo", "Primera comunión", "Confirmación"]),
};

const entryLevels = [
  ["initial", "Nivel Inicial"],
  ["primary", "Nivel Primario"],
  ["secondary", "Nivel Secundario"],
];

export default function EnrollmentFields({
  documentType,
  fields,
  values,
  onChange,
  onStudentDniBlur,
  disabled,
  hiddenFieldIds = new Set(),
  showAnexoAuthorizationInfo = false,
}) {
  const visibleSacramentSections = sacramentSectionsByEntryLevel[values.entry_level];
  const visibleSections = sections[documentType].filter(
    ([title, pattern]) =>
      (!["Bautismo", "Primera comunión", "Confirmación"].includes(title) ||
        visibleSacramentSections?.has(title)) &&
      fields.some((field) => pattern.test(field.id) && !hiddenFieldIds.has(field.id))
  );

  return (
    <>
      {visibleSections.map(([title, pattern]) => (
        <Fragment key={title}>
          {showAnexoAuthorizationInfo &&
            documentType === "anexo" &&
            title === "Persona autorizada 1" && (
              <aside
                className="enrollment-fields__authorization-note"
                aria-label="Información sobre las personas autorizadas"
              >
                <strong>Retiro del alumno/a</strong>
                <p>
                  En el caso que no pueda retirar personalmente al alumno/a, autorizo para el retiro
                  del mismo a las personas que se detallan a continuación o al transporte escolar.
                </p>
              </aside>
            )}
          <fieldset className="enrollment-fields__section" disabled={disabled}>
            <legend>{title}</legend>
            <div className="enrollment-fields__grid">
              {fields
                .filter((field) => pattern.test(field.id) && !hiddenFieldIds.has(field.id))
                .sort((first, second) => {
                  if (first.id === "student_dni") return -1;
                  if (second.id === "student_dni") return 1;
                  return 0;
                })
                .map((field) => (
                  <div
                    className={
                      field.type === "checkbox"
                        ? "enrollment-fields__choice"
                        : "enrollment-fields__field"
                    }
                    key={field.id}
                  >
                    {field.type === "checkbox" ? (
                      <>
                        <input
                          id={`entry-${field.id}`}
                          type="checkbox"
                          checked={Boolean(values[field.id])}
                          onChange={(event) => onChange(field, event.target.checked)}
                        />
                        <label htmlFor={`entry-${field.id}`}>{field.label}</label>
                      </>
                    ) : (
                      <>
                        <label htmlFor={`entry-${field.id}`}>{field.label}</label>
                        <input
                          id={`entry-${field.id}`}
                          type={field.id.includes("date") ? "date" : "text"}
                          inputMode={
                            field.id.includes("phone")
                              ? "tel"
                              : field.id.includes("email")
                              ? "email"
                              : "text"
                          }
                          value={
                            field.id.includes("date")
                              ? toDateInputValue(values[field.id])
                              : typeof values[field.id] === "string"
                              ? values[field.id]
                              : ""
                          }
                          onChange={(event) => onChange(field, event.target.value)}
                          onBlur={
                            field.id === "student_dni"
                              ? (event) => onStudentDniBlur?.(event.target.value)
                              : undefined
                          }
                          autoFocus={field.id === "student_dni"}
                          aria-describedby={
                            field.id === "student_dni" ? `hint-${field.id}` : undefined
                          }
                        />
                        {field.id === "student_dni" && (
                          <small id={`hint-${field.id}`}>
                            Al salir de este campo, buscamos los datos guardados.
                          </small>
                        )}
                      </>
                    )}
                  </div>
                ))}
            </div>
          </fieldset>
          {documentType === "solicitud" && title === "Datos del alumno/a" && (
            <fieldset className="enrollment-fields__section" disabled={disabled}>
              <legend>Nivel al que ingresa</legend>
              <div
                className="enrollment-fields__level-options"
                role="radiogroup"
                aria-label="Nivel al que ingresa"
              >
                {entryLevels.map(([level, label]) => (
                  <label className="enrollment-fields__level-option" key={level}>
                    <input
                      type="radio"
                      name="entry_level"
                      value={level}
                      checked={values.entry_level === level}
                      onChange={() => onChange({ id: "entry_level" }, level)}
                    />
                    {label}
                  </label>
                ))}
              </div>
              {!visibleSacramentSections && (
                <small className="enrollment-fields__level-hint">
                  Seleccioná el nivel para ver los datos sacramentales que corresponden.
                </small>
              )}
            </fieldset>
          )}
        </Fragment>
      ))}
    </>
  );
}
